/**
 * Permission Context for Frontend
 * Provides user permissions and role-based access control throughout the application
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { UserPermissions, UserRole, AuthenticatedUser, SectionPermissions } from '../lib/types';
import { useAuthProvider } from '../hooks/use-azure-auth-provider';
import { apiRequest } from '../lib/api';

/**
 * Permission context interface
 */
interface PermissionContextType {
  user: AuthenticatedUser | null;
  authUser: any; // The raw auth user from Azure AD
  permissions: UserPermissions | null;
  isLoading: boolean;
  error: string | null;

  // Permission checking functions
  hasPermission: (section: keyof UserPermissions, operation: keyof SectionPermissions) => boolean;
  canView: (section: keyof UserPermissions) => boolean;
  canEdit: (section: keyof UserPermissions) => boolean;
  canExecute: (section: keyof UserPermissions) => boolean;
  canExport: (section: keyof UserPermissions) => boolean;

  // Role checking functions
  isAdmin: () => boolean;
  canManageUsers: () => boolean;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;

  // Utility functions
  refresh: () => Promise<void>;
}

/**
 * Create the permission context
 */
const PermissionContext = createContext<PermissionContextType | null>(null);

/**
 * Permission provider component
 */
interface PermissionProviderProps {
  children: ReactNode;
}

export function PermissionProvider({ children }: PermissionProviderProps) {
  const { isSignedIn: isAuthenticated, user: authUser } = useAuthProvider();
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  /**
   * Fetch user permissions from the API
   */
  const fetchUserPermissions = async () => {
    if (!isAuthenticated || !authUser || isFetching || retryCount >= MAX_RETRIES) {
      if (!isAuthenticated || !authUser) {
        setUser(null);
        setPermissions(null);
        setIsLoading(false);
        setRetryCount(0); // Reset retry count when auth state changes
      } else if (retryCount >= MAX_RETRIES) {
        console.error(`[PermissionContext] Max retries (${MAX_RETRIES}) exceeded, stopping fetch attempts`);
        setError(`Failed to load user permissions after ${MAX_RETRIES} attempts. Please refresh the page.`);
        setIsLoading(false);
      }
      return;
    }

    try {
      setIsFetching(true);
      setIsLoading(true);
      setError(null);

      // Call the user management API to get current user's permissions
      const response = await apiRequest('GET', `/api/users/${authUser.id}`);

      if (response.ok) {
        try {
          const userData = await response.json();
          if (userData.user) {
            setUser(userData.user);
            setPermissions(userData.user.permissions);
          }
        } catch (jsonError) {
          console.error('Failed to parse success response as JSON:', jsonError);
          throw new Error('Invalid response format from server');
        }
      } else if (response.status === 403) {
        // User not authorized - might need bootstrap
        try {
          const errorData = await response.json();
          setError(errorData.message || 'Access denied. Your account may need to be set up by an administrator.');
        } catch {
          setError('Access denied. Your account may need to be set up by an administrator.');
        }
      } else if (response.status === 404) {
        // User not found in system - needs to be added
        setError('Your account is not set up in the system. Please contact an administrator.');
      } else {
        // Try to get error message from response, but don't fail if JSON parsing fails
        let errorMessage = `Failed to fetch user permissions: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // Ignore JSON parsing errors for error responses
        }
        throw new Error(errorMessage);
      }
    } catch (err: any) {
      console.error('Error fetching user permissions:', err);

      // Check if circuit breaker is open - don't retry immediately
      if (err.name === 'CircuitBreakerOpen') {
        setError('API is temporarily unavailable. Will retry when connection is restored.');
        // Don't increment retry count for circuit breaker errors
        // The circuit breaker will automatically retry after its timeout
      } else {
        setRetryCount(prev => prev + 1);
        const newRetryCount = retryCount + 1;

        if (newRetryCount >= MAX_RETRIES) {
          setError(`Failed to load user permissions after ${MAX_RETRIES} attempts: ${err.message || 'Unknown error'}`);
        } else {
          setError(err.message || 'Failed to load user permissions');
          // Add exponential backoff for retries
          setTimeout(() => {
            if (newRetryCount < MAX_RETRIES) {
              fetchUserPermissions();
            }
          }, Math.pow(2, newRetryCount) * 1000); // 2s, 4s, 8s backoff
        }
      }
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  };

  /**
   * Initialize permissions when auth state changes
   */
  useEffect(() => {
    setRetryCount(0); // Reset retry count on auth state change
    fetchUserPermissions();
  }, [isAuthenticated, authUser?.id]); // Use authUser.id instead of entire authUser object

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (section: keyof UserPermissions, operation: keyof SectionPermissions): boolean => {
    if (!permissions || !permissions[section]) {
      return false;
    }

    const sectionPermissions = permissions[section];
    return sectionPermissions && sectionPermissions[operation] === true;
  };

  /**
   * Check if user can view a section
   */
  const canView = (section: keyof UserPermissions): boolean => {
    return hasPermission(section, 'view');
  };

  /**
   * Check if user can edit in a section
   */
  const canEdit = (section: keyof UserPermissions): boolean => {
    return hasPermission(section, 'edit');
  };

  /**
   * Check if user can execute in a section
   */
  const canExecute = (section: keyof UserPermissions): boolean => {
    return hasPermission(section, 'execute');
  };

  /**
   * Check if user can export from a section
   */
  const canExport = (section: keyof UserPermissions): boolean => {
    return hasPermission(section, 'export');
  };

  /**
   * Check if user is admin
   */
  const isAdmin = (): boolean => {
    return user?.role === UserRole.Admin || (user?.roleNames || []).includes(UserRole.Admin);
  };

  /**
   * Check if user can manage other users
   */
  const canManageUsers = (): boolean => {
    return hasPermission('userManagement', 'edit');
  };

  /**
   * Check if user has a specific role
   */
  const hasRole = (role: UserRole): boolean => {
    return user?.role === role || (user?.roleNames || []).includes(role);
  };

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (roles: UserRole[]): boolean => {
    if (!user) return false;
    if (roles.includes(user.role)) return true;
    const userRoleNames = user.roleNames || [];
    return roles.some(r => userRoleNames.includes(r));
  };

  /**
   * Refresh user permissions
   */
  const refresh = async (): Promise<void> => {
    if (!isFetching) {
      await fetchUserPermissions();
    }
  };

  const contextValue: PermissionContextType = {
    user,
    authUser,
    permissions,
    isLoading,
    error,
    hasPermission,
    canView,
    canEdit,
    canExecute,
    canExport,
    isAdmin,
    canManageUsers,
    hasRole,
    hasAnyRole,
    refresh
  };

  // If there's an error, show the unauthorized screen globally
  if (error) {
    return (
      <PermissionContext.Provider value={contextValue}>
        <PermissionError
          error={error}
          userId={authUser?.id}
        />
      </PermissionContext.Provider>
    );
  }

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
}

/**
 * Hook to use permissions
 */
export function usePermissions(): PermissionContextType {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}

/**
 * Higher-order component for permission-based rendering
 */
interface WithPermissionsProps {
  children: ReactNode;
  section: keyof UserPermissions;
  operation: keyof SectionPermissions;
  fallback?: ReactNode;
}

export function WithPermissions({ children, section, operation, fallback = null }: WithPermissionsProps) {
  const { hasPermission, isLoading } = usePermissions();

  if (isLoading) {
    return <div>Loading permissions...</div>;
  }

  if (!hasPermission(section, operation)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

/**
 * Higher-order component for role-based rendering
 */
interface WithRoleProps {
  children: ReactNode;
  roles: UserRole | UserRole[];
  fallback?: ReactNode;
}

export function WithRole({ children, roles, fallback = null }: WithRoleProps) {
  const { hasRole, hasAnyRole, isLoading } = usePermissions();

  if (isLoading) {
    return <div>Loading permissions...</div>;
  }

  const rolesArray = Array.isArray(roles) ? roles : [roles];
  const hasRequiredRole = rolesArray.length === 1 ? hasRole(rolesArray[0]) : hasAnyRole(rolesArray);

  if (!hasRequiredRole) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

/**
 * Component for handling permission errors and bootstrap
 */
interface PermissionErrorProps {
  error: string;
  userId?: string;
  onBootstrap?: () => void;
}

export function PermissionError({ error, userId, onBootstrap }: PermissionErrorProps) {
  const { instance } = useMsal();
  const showBootstrap = error.toLowerCase().includes('administrator') || error.toLowerCase().includes('set up');

  const handleSignOut = async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.96-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="mt-2 text-lg font-semibold text-gray-900">You are not authorized to access this system</h3>
          <div className="mt-4 text-sm text-gray-600">
            {userId && (
              <div className="mb-4">
                <p className="mb-2">Your User ID:</p>
                <div className="bg-gray-50 rounded-md p-3 border">
                  <p className="text-gray-900 font-mono text-xs break-all">{userId}</p>
                </div>
              </div>
            )}
            <p className="mb-2">Please email the administrator for access and include your User ID:</p>
            <div className="bg-gray-50 rounded-md p-3 border">
              <p className="font-medium text-gray-900">Email or Teams:</p>
              <p className="text-blue-600 font-mono">j.cook@peterlang.com</p>
            </div>
          </div>
          <div className="mt-6 flex flex-col gap-3">
            {showBootstrap && onBootstrap && (
              <button
                onClick={onBootstrap}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Set up as Administrator
              </button>
            )}
            <button
              onClick={handleSignOut}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
